import { css, cx, keyframes } from "@emotion/css";
import { GemIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type React from "react";
import { useLayoutEffect, useMemo, useRef } from "react";
const badgeStyles = css({
    display: "inline-flex",
    borderRadius: borderRadius.circle,
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
});
const variantStyles: {
    [key in BadgeVariant]: string;
} = {
    danger: css({
        backgroundColor: themeTokens.color.badge.background.danger,
        color: themeTokens.color.badge.text.primary,
        "--badge-pulse-color": themeTokens.color.badge.background.danger,
    }),
    neutral: css({
        backgroundColor: themeTokens.color.badge.background.neutral,
        color: themeTokens.color.badge.text.secondary,
        "--badge-pulse-color": themeTokens.color.badge.background.neutral,
    }),
    success: css({
        backgroundColor: themeTokens.color.badge.background.success,
        color: themeTokens.color.badge.text.primary,
        "--badge-pulse-color": themeTokens.color.badge.background.success,
    }),
    info: css({
        backgroundColor: themeTokens.color.badge.background.info,
        color: themeTokens.color.badge.text.primary,
        "--badge-pulse-color": themeTokens.color.badge.background.info,
    }),
    warning: css({
        backgroundColor: themeTokens.color.badge.background.warning,
        color: themeTokens.color.badge.text.primary,
        "--badge-pulse-color": themeTokens.color.badge.background.warning,
    }),
    inverseNeutral: css({
        backgroundColor: themeTokens.color.badge.background.inverseNeutral,
        color: themeTokens.color.badge.text.primary,
        "--badge-pulse-color": themeTokens.color.badge.background.inverseNeutral,
    }),
    feature: css({
        backgroundColor: themeTokens.color.badge.background.feature,
        color: themeTokens.color.badge.text.feature,
        "--badge-pulse-color": themeTokens.color.badge.background.feature,
    }),
    upsell: css({
        backgroundColor: themeTokens.color.badge.background.upsell,
        color: themeTokens.color.badge.text.upsell,
        "--badge-pulse-color": themeTokens.color.badge.background.upsell,
    }),
};
type BadgeSize = BadgeProps["size"];
const sizeStyles: {
    [key in BadgeSize]: string;
} = {
    xSmall: css({
        height: "16px",
        minWidth: "16px",
        font: text.body.regular.xSmall,
        fontSize: "12px",
        padding: `0 ${space[4]}`,
    }),
    small: css({
        height: "20px",
        minWidth: "20px",
        font: text.body.regular.xSmall,
        padding: `0 ${space[6]}`,
        gap: space[4],
    }),
    medium: css({
        height: "24px",
        minWidth: "24px",
        font: text.body.regular.small,
        padding: `0 ${space[8]} 0 ${space[6]}`,
        gap: space[6],
    }),
};
export type BadgeVariant = "neutral" | "info" | "success" | "warning" | "danger" | "inverseNeutral" | "feature" | "upsell";
interface SharedBadgeProps {
    variant: BadgeVariant;
}
export interface SmallOrMediumBadgePropsWithIcon extends SharedBadgeProps {
    size: "small" | "medium";
    count?: number;
    icon?: React.ReactNode;
    label?: string;
    variant: Exclude<BadgeVariant, "upsell">;
    statusAnimation?: boolean;
}
export interface SmallOrMediumBadgePropsUpsell extends SharedBadgeProps {
    size: "small" | "medium";
    count?: number;
    label?: string;
    variant: "upsell";
    hideIcon?: boolean;
    statusAnimation?: boolean;
}
export type SmallOrMediumBadgeProps = SmallOrMediumBadgePropsWithIcon | SmallOrMediumBadgePropsUpsell;
export interface ExtraSmallBadgeProps extends SharedBadgeProps {
    size: "xSmall";
    count?: number;
    label?: string;
    statusAnimation?: boolean;
}
export type BadgeProps = SmallOrMediumBadgeProps | ExtraSmallBadgeProps;
interface StatusAnimationDeps {
    variant: BadgeVariant;
    label: string | undefined;
    count: number | undefined;
}
/**
 * Returns a key that changes when badge status (variant/label/count) changes, and whether
 * to run the status animation. Animation runs only when statusAnimation is true and the key
 * has changed since the previous render (not on first mount).
 */
function useStatusAnimationKey(statusAnimation: boolean | undefined, deps: StatusAnimationDeps): {
    animationKey: string;
    shouldAnimate: boolean;
} {
    const animationKey = useMemo(() => {
        if (!statusAnimation)
            return "static";
        return `${deps.label ?? ""}-${deps.count ?? ""}`;
    }, [statusAnimation, deps.label, deps.count]);
    const prevKeyRef = useRef<string | undefined>(undefined);
    const prevKey = prevKeyRef.current;
    const shouldAnimate = statusAnimation === true && prevKey !== undefined && prevKey !== animationKey;
    useLayoutEffect(() => {
        prevKeyRef.current = animationKey;
    }, [animationKey]);
    return { animationKey, shouldAnimate };
}
export function Badge(props: BadgeProps) {
    return props.size === "xSmall" ? <ExtraSmallBadge size={props.size} count={props.count} label={props.label} variant={props.variant}/> : <SmallOrMediumBadge {...props}/>;
}
function ExtraSmallBadge(props: ExtraSmallBadgeProps) {
    const { animationKey, shouldAnimate } = useStatusAnimationKey(props.statusAnimation, { variant: props.variant, label: props.label, count: props.count });
    return (<div key={animationKey} className={cx(badgeStyles, variantStyles[props.variant], sizeStyles[props.size], { [statusAnimationStyles]: shouldAnimate })}>
            <BadgeLabel label={props.label} count={props.count} size={props.size}/>
        </div>);
}
function SmallOrMediumBadge(props: SmallOrMediumBadgeProps) {
    const { animationKey, shouldAnimate } = useStatusAnimationKey(props.statusAnimation, { variant: props.variant, label: props.label, count: props.count });
    let icon: React.ReactNode = undefined;
    if (props.variant === "upsell") {
        if (props.hideIcon !== true) {
            const iconSize = props.size === "medium" ? 20 : 16;
            icon = <GemIcon size={iconSize} color={themeTokens.color.badge.icon.upsell}/>;
        }
    }
    else if ("icon" in props) {
        icon = props.icon;
    }
    return (<div key={animationKey} className={cx(badgeStyles, variantStyles[props.variant], sizeStyles[props.size], { [statusAnimationStyles]: shouldAnimate, [countOnlyBadgeStyles]: !icon && !props.label })}>
            {icon && <BadgeIcon size={props.size} icon={icon}/>}
            <BadgeLabel label={props.label} count={props.count} size={props.size}/>
        </div>);
}
const smallBadgeIconStyles = css({
    width: "16px",
    height: "16px",
});
const mediumBadgeIconStyles = css({
    width: "20px",
    height: "20px",
});
const badgeStatusAnimationKeyFrames = keyframes({
    "0%": {
        backgroundColor: "color-mix(in srgb, var(--badge-pulse-color) 65%, black)",
        boxShadow: "0 0 0 0 color-mix(in srgb, var(--badge-pulse-color) 60%, transparent)",
    },
    "70%": {
        boxShadow: "0 0 0 5px transparent",
    },
    "100%": {
        backgroundColor: "var(--badge-pulse-color)",
        boxShadow: "0 0 0 0 transparent",
    },
});
const statusAnimationStyles = css({
    animationName: `${badgeStatusAnimationKeyFrames}`,
    animationDuration: ".75s",
    animationIterationCount: 1,
    animationFillMode: "forwards",
    animationTimingFunction: "ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
    },
});
interface BadgeIconProps {
    icon: React.ReactNode;
    size: SmallOrMediumBadgeProps["size"];
}
function BadgeIcon({ icon, size }: BadgeIconProps) {
    return <div className={size === "medium" ? mediumBadgeIconStyles : smallBadgeIconStyles}>{icon}</div>;
}
interface BadgeLabelProps {
    label?: string;
    count?: number;
    size: "xSmall" | "small" | "medium";
}
const badgeLabelStyles = css({
    display: "flex",
    gap: space[4],
    justifyContent: "center",
    alignItems: "center",
});
const xSmallLabelStyles = css({
    fontWeight: fontWeight[600],
    fontSize: "12px",
    lineHeight: "16px",
});
function BadgeLabel({ label, count, size }: BadgeLabelProps) {
    return (<span className={cx(badgeLabelStyles, { [xSmallLabelStyles]: size === "xSmall" })}>
            {count !== undefined && <BadgeCount count={count} size={size}/>}
            {label}
        </span>);
}
const badgeCountStyles = css({
    display: "flex",
    justifyItems: "center",
    alignItems: "center",
});
const badgeCountSizeStyles: {
    [key in BadgeSize]: string;
} = {
    xSmall: css({
        fontWeight: fontWeight[600],
        fontSize: "12px",
        lineHeight: "16px",
    }),
    small: css({
        font: text.body.regular.xSmall,
    }),
    medium: css({
        font: text.body.bold.small,
    }),
};
const countOnlyBadgeStyles = css({
    padding: `0 ${space[4]}`,
});
function BadgeCount({ count, size }: {
    count: number;
    size: BadgeSize;
}) {
    return <span className={cx(badgeCountStyles, badgeCountSizeStyles[size])}>{count}</span>;
}
